import { useCallback, useEffect, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { api } from '@/services/api';
import { Button } from '@/components/ui/button';
import { Card, CardHeader } from '@/components/ui/card';
import { Drawer } from '@/components/ui/drawer';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Toggle } from '@/components/ui/toggle';
import { DataTable } from '@/components/tables/data-table';
import { EmptyState } from '@/components/ui/empty-state';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { StatusBadge } from '@/components/ui/status-badge';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';

const tone = (status) => status === 'active' ? 'success' : status === 'blocked' ? 'error' : status === 'disabled' ? 'warning' : 'neutral';
const initialForm = { name: '', username: '', email: '', phoneNumber: '', status: 'active', locationSharingEnabled: false };
const columns = [
  { key: 'name', header: 'Rider', sortValue: (row) => row.name, cell: (row) => <div><p className="text-xs font-bold text-slate-100">{row.name}</p><p className="text-[10px] text-slate-600">@{row.username}</p></div> },
  { key: 'email', header: 'Google account', cell: (row) => <span className="text-xs text-slate-400">{row.email}</span> },
  { key: 'authProvider', header: 'Login', cell: (row) => <span className="text-[9px] font-black uppercase tracking-[.12em] text-cyan-300">{row.oauthLinked ? 'Google linked' : 'Awaiting Google'}</span> },
  { key: 'status', header: 'Status', cell: (row) => <StatusBadge tone={tone(row.status)}>{row.status}</StatusBadge> },
  { key: 'lastLoginAt', header: 'Last login', sortValue: (row) => row.lastLoginAt, cell: (row) => <span className="text-[10px] text-slate-500">{row.lastLoginAt ? new Date(row.lastLoginAt).toLocaleString() : 'Never'}</span> },
];

export function MembersPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('all');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const query = new URLSearchParams({ page: '1', limit: '200', ...(status !== 'all' ? { status } : {}) });
      const result = await api(`/api/v1/admin/members?${query}`);
      setRows(result.data?.items ?? result.data ?? []);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Members could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { void load(); }, [load]);

  const create = async () => {
    if (!validForm(form)) return;
    try {
      setSaving(true);
      await api('/api/v1/admin/members', { method: 'POST', body: JSON.stringify({ ...form, phoneNumber: form.phoneNumber || undefined }) });
      setOpen(false);
      setForm(initialForm);
      await load();
    } finally {
      setSaving(false);
    }
  };

  return <div className="workspace-page">
    <PageHeader eyebrow="Directory" title="Riders" description="Riders authenticate with their registered Google account." meta={<Badge tone="accent">{rows.length} loaded</Badge>} actions={<Button className="h-8 px-3 text-xs" onClick={() => setOpen(true)}><UserPlus size={14}/>Add rider</Button>}/>
    <Card className="workspace-fill">
      <CardHeader eyebrow="Identity directory" title="Rider accounts"/>
      {error ? <EmptyState title="Members unavailable" action={<Button variant="secondary" onClick={() => void load()}>Retry</Button>}/> : <DataTable
        tableId="members"
        columns={columns}
        rows={rows}
        getKey={(row) => row._id}
        loading={loading}
        onRefresh={() => load()}
        searchPlaceholder="Search name, email, or username"
        filters={[{
          key: 'status',
          label: 'Status',
          value: status === 'all' ? '' : status,
          onClear: () => setStatus('all'),
          render: <Select className="h-9 text-xs" value={status} onChange={(event) => setStatus(event.target.value)}><option value="all">All status</option><option value="active">Active</option><option value="disabled">Disabled</option><option value="blocked">Blocked</option></Select>,
        }]}
      />}
    </Card>
    <Drawer open={open} onClose={() => setOpen(false)} title="Add rider" eyebrow="Directory" description="Use the rider's Google account email.">
      <div className="space-y-4">
        <Field label="Name" required><Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Rider name"/></Field>
        <Field label="Username" required hint="Lowercase letters, numbers, dot, dash, underscore"><Input value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value.toLowerCase() })} placeholder="rider.name"/></Field>
        <Field label="Google email" required hint="Must match the mobile account"><Input type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value.toLowerCase() })} placeholder="rider@gmail.com"/></Field>
        <Field label="Phone"><Input value={form.phoneNumber} onChange={(event) => setForm({ ...form, phoneNumber: event.target.value })} placeholder="+628..."/></Field>
        <div className="grid gap-4 sm:grid-cols-2"><Field label="Initial status"><Select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}><option value="active">Active</option><option value="disabled">Disabled</option><option value="blocked">Blocked</option></Select></Field><div className="flex items-end"><Toggle checked={form.locationSharingEnabled} onChange={(checked) => setForm({ ...form, locationSharingEnabled: checked })} label="Location sharing consent"/></div></div>
        <Button className="w-full" disabled={saving || !validForm(form)} onClick={create}>{saving ? 'Adding rider' : 'Add rider'}</Button>
      </div>
    </Drawer>
    <LoadingOverlay open={saving} label="Adding rider"/>
  </div>;
}

function validForm(form) {
  return form.name.trim().length >= 2 && /^[a-z0-9._-]{3,40}$/.test(form.username) && /.+@.+\..+/.test(form.email) && (!form.phoneNumber || /^\+[1-9]\d{7,14}$/.test(form.phoneNumber));
}
